import { useEffect } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { BarChart3 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FadeIn } from '@/components/motion';
import { useAnalyticsStore } from '../store/analytics-store';
import { PeriodSelector } from '../components/period-selector';
import { KpiCards } from '../components/kpi-cards';
import { ChartsSection } from '../components/charts-section';
import { TopSellersTable } from '../components/top-sellers-table';
import { ConversionFunnel } from '../components/conversion-funnel';
import { MarginsTab } from '../components/margins-tab';
import { WinLossTab } from '../components/win-loss-tab';
import { DiscountTab } from '../components/discount-tab';
import { ModelsTab } from '../components/models-tab';
import { TrendsTab } from '../components/trends-tab';
import { WaterfallTab } from '../components/waterfall-tab';
import type { AnalyticsTab } from '../types/analytics.types';

export function AnalyticsPage() {
  const {
    data,
    period,
    isLoading,
    isLoadingTab,
    activeTab,
    marginsData,
    winLossData,
    discountData,
    modelsData,
    trendsData,
    waterfallData,
    setPeriod,
    setActiveTab,
    fetchOverview,
  } = useAnalyticsStore();

  useEffect(() => {
    fetchOverview();
  }, []);

  const handleTabChange = (value: string) => {
    setActiveTab(value as AnalyticsTab);
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Cotiza (artefacto, 19-oct). */}
        <div data-tour="analytics-header">
          <PageHeader
            modulo="cotiza"
            etiqueta="Cotiza"
            icono={BarChart3}
            titulo="Analítica"
            subtitulo="Cotizaciones, márgenes y rendimiento comercial, en detalle."
            acciones={
              <div data-tour="analytics-filters">
                <PeriodSelector value={period} onChange={setPeriod} />
              </div>
            }
          />
        </div>

        {/* KPI Cards (always visible) */}
        <FadeIn delay={0.1}>
          <div data-tour="analytics-kpis">
            <KpiCards data={data?.kpis ?? null} isLoading={isLoading} />
          </div>
        </FadeIn>

        {/* Tabs */}
        <FadeIn delay={0.2}>
          <Tabs data-tour="analytics-charts" value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="overview">Resumen</TabsTrigger>
              <TabsTrigger value="margins">Márgenes</TabsTrigger>
              <TabsTrigger value="win-loss">Ganadas y perdidas</TabsTrigger>
              <TabsTrigger value="discounts">Descuentos</TabsTrigger>
              <TabsTrigger value="models">Modelos</TabsTrigger>
              <TabsTrigger value="trends">Tendencias</TabsTrigger>
              <TabsTrigger value="waterfall">Cascada</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {data && (
                <>
                  <ChartsSection
                    revenueByMonth={data.revenueByMonth}
                    quotesByStatus={data.quotesByStatus}
                    revenueByLob={data.revenueByLob}
                    marginDistribution={data.marginDistribution}
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <TopSellersTable sellers={data.topSellers} />
                    <ConversionFunnel funnel={data.conversionFunnel} />
                  </div>
                </>
              )}
              {!data && !isLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  Sin datos disponibles para este periodo
                </div>
              )}
            </TabsContent>

            {/* Margins Tab */}
            <TabsContent value="margins">
              <MarginsTab data={marginsData} isLoading={isLoadingTab} />
            </TabsContent>

            {/* Win/Loss Tab */}
            <TabsContent value="win-loss">
              <WinLossTab data={winLossData} isLoading={isLoadingTab} />
            </TabsContent>

            {/* Discounts Tab */}
            <TabsContent value="discounts">
              <DiscountTab data={discountData} isLoading={isLoadingTab} />
            </TabsContent>

            {/* Models Tab */}
            <TabsContent value="models">
              <ModelsTab data={modelsData} isLoading={isLoadingTab} />
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends">
              <TrendsTab data={trendsData} isLoading={isLoadingTab} />
            </TabsContent>

            {/* Waterfall Tab */}
            <TabsContent value="waterfall">
              <WaterfallTab data={waterfallData} isLoading={isLoadingTab} />
            </TabsContent>
          </Tabs>
        </FadeIn>
      </div>
    </AppLayout>
  );
}

export default AnalyticsPage;
